// src/components/BusinessList.js

import React, { useEffect, useState, useRef, useMemo } from 'react';
import LoadingSpinner from './LoadingSpinner';
import FilterControls from './FilterControls';
import CardList from './CardList';
import useInfiniteScroll from '../hooks/useInfiniteScroll';
import { fetchBusinessesData } from '../utils/api';
import './BusinessList.styles.scss';

const ITEMS_PER_PAGE = 8;

export default function BusinessList() {
  const [businesses, setBusinesses]           = useState([]);
  const [loading, setLoading]                 = useState(true);
  const [showNoWebsite, setShowNoWebsite]     = useState(false);
  const [showWardFilter, setShowWardFilter]   = useState(false);
  const [selectedWard, setSelectedWard]       = useState('');
  const [sortOrder, setSortOrder]             = useState('');    // ← new
  const containerRef                          = useRef(null);

  // 1) fetch all businesses on mount
  useEffect(() => {
    (async () => {
      try {
        const data = await fetchBusinessesData();
        setBusinesses(data);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // 2) compute unique ward list
  const wards = useMemo(() => {
    return Array.from(
      new Set(businesses.map(b => String(b.attributes.Ward)))
    ).sort((a, b) =>
      a.localeCompare(b, undefined, { numeric: true })
    );
  }, [businesses]);

  // 3) apply the two filters
  const filtered = useMemo(() => {
    return businesses
      .filter(b =>
        showNoWebsite
          ? (b.attributes.WebAddres || '').trim() === ''
          : true
      )
      .filter(b =>
        showWardFilter && selectedWard
          ? String(b.attributes.Ward) === selectedWard
          : true
      );
  }, [businesses, showNoWebsite, showWardFilter, selectedWard]);

  // 4) sort A–Z or Z–A by Name
  const sorted = useMemo(() => {
    if (!sortOrder) return filtered;
    const copy = [...filtered];
    return copy.sort((a, b) => {
      const nameA = a.attributes.Name || '';
      const nameB = b.attributes.Name || '';
      return sortOrder === 'asc'
        ? nameA.localeCompare(nameB)
        : nameB.localeCompare(nameA);
    });
  }, [filtered, sortOrder]);

  // 5) infinite‐scroll slice of the sorted list
  const visible = useInfiniteScroll(sorted, ITEMS_PER_PAGE, containerRef) || [];

  if (loading) return <LoadingSpinner />;

  return (
    <div className="business-list-container" ref={containerRef}>
      <h2 className="container-title">Mississauga Directory</h2>

      <FilterControls
        showNoWebsite={showNoWebsite}
        onToggleNoWebsite={() => setShowNoWebsite(v => !v)}
        showWardFilter={showWardFilter}
        onToggleWardFilter={() => {
          setShowWardFilter(v => !v);
          setSelectedWard('');
        }}
        wards={wards}
        selectedWard={selectedWard}
        onSelectWard={setSelectedWard}

        sortOrder={sortOrder}           
        onSortChange={setSortOrder}    
      />

      {filtered.length === 0 ? (
        <p className="no-results">No matching businesses found.</p>
      ) : (
        <CardList items={visible} />
      )}
    </div>
  );
}
